import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';

import {
  ApplicationInsights,
  IConfiguration,
  IConfig,
  DistributedTracingModes,
  ITelemetryItem,
  IEventTelemetry,
  IPageViewTelemetry,
  IPageViewPerformanceTelemetry,
  IExceptionTelemetry,
  ITraceTelemetry,
  IMetricTelemetry,
  IDependencyTelemetry
} from '@microsoft/applicationinsights-web';

import { ICustomProperties } from '@microsoft/applicationinsights-core-js';

export const HEADER_DOMAINS = [environment.apiURLRC, environment.apiURLROCT].map(item => {

  // Cleans up Headers to get raw URLs.
  const https = 'https://';
  const removeHttps = item.includes(https) ? item.substring(https.length - 1) : item;

  return removeHttps[removeHttps.length - 1] === '/' ? removeHttps.substring(0, removeHttps.length - 1) : removeHttps;
}).map(item => item[0] === '/' ? item.substring(1) : item);

export const DEFAULT_INSIGHTS_CONFIG: IConfiguration & IConfig = {
  instrumentationKey: 'RRSM.RC',
  endpointUrl: `${environment.apiURLRC}Audits/api/Audits`,
  enableCorsCorrelation: true,
  disableCorrelationHeaders: false,
  enableAutoRouteTracking: true,
  distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
  autoTrackPageVisitTime: true,
  enableRequestHeaderTracking: true,
  enableResponseHeaderTracking: true,
  enableDebugExceptions: true,
  disableExceptionTracking: false,
  namePrefix: 'RRSM.RC',
  correlationHeaderDomains: HEADER_DOMAINS,
  disableInstrumentaionKeyValidation: true,
};

@Injectable({
  providedIn: 'root'
})
export class AppInsightsService {

  private insightsConfig: IConfiguration & IConfig = DEFAULT_INSIGHTS_CONFIG;
  private insights: ApplicationInsights;

  constructor() {
    this.insights = new ApplicationInsights({
      config: this.insightsConfig
    });
    this.insights.loadAppInsights();
  }

  /// Insights Configuration Management
  // #region Insights Configutation

  /**
   * Updates the application insight config, by property.
   * @param config configuration fields to update
   */
  public updateInsightsConfig(config: Partial<IConfiguration & IConfig>): void {
    this.insightsConfig = { ...this.insightsConfig, ...config };
    this.insights.config = this.insightsConfig;
  }
  // #endregion Insights Configutation

  /// User Context Management
  // #region User Context

  /**
   * Set the User Context to the specified userId
   * @param userId the user id to update the user context with.
   */
  public setUser(userId: string): void {
    this.insights.setAuthenticatedUserContext(userId);
  }

  /**
   * Clears the User Context
   */
  public clearUser(): void {
    this.insights.clearAuthenticatedUserContext();
  }
  // #endregion User Context

  /// Wrapped Tracking Functions
  // #region Tracking Functions

  /**
   * Targets the AUDT.Event Table
   *
   * Log a user action or other occurrence.
   * @param event Event Data to Log or the name of the event
   * @param customProperties Custom Properties of Event
   */
  public logEvent(event: IEventTelemetry, customProperties?: ICustomProperties): void {

    this.insights.trackEvent(event, customProperties);

  }

  /**
   * Targets the AUDT.PageView Table
   *
   * Logs that a page, or similar container was displayed to the user.
   * @param pageView Page View Data to Log
   */
  public logPageView(pageView?: IPageViewTelemetry): void {
    this.insights.addTelemetryInitializer(this.telemetryInitializer);
    this.insights.trackPageView(pageView);
  }

  /**
   * Targets the AUDT.PageViewPerformance Table
   *
   * Log a bag of performance information via the customProperties field.
   * @param pageViewPerformance Page View Performance Data to Log
   */
  public logPageViewPerformance(pageViewPerformance: IPageViewPerformanceTelemetry): void {
    this.insights.trackPageViewPerformance(pageViewPerformance);
  }

  /**
   * Targets the AUDT.Exception Table
   *
   * Log an exception that you have caught.
   * @param exception Exception Telemetry to Log
   */
  public logException(exception: IExceptionTelemetry): void {
    this.insights.trackException(exception);
  }

  /**
   * Targets the AUDT.Trace Table
   *
   * Log a diagnostic scenario such entering or leaving a function.
   * @param trace Trace Data to Log
   * @param customProperties Custom Properties of Trace
   */
  public logTrace(trace: ITraceTelemetry, customProperties?: ICustomProperties): void {
    this.insights.trackTrace(trace, customProperties);
  }

  /**
   * Targets the AUDT.Metric Table
   *
   * Log a numeric value that is not associated with a specific event. Typically used
   * to send regular reports of performance indicators.
   *
   * To send a single measurement, just use the `name` and `average` fields
   * of {@link IMetricTelemetry}.
   *
   * If you take measurements frequently, you can reduce the telemetry bandwidth by
   * aggregating multiple measurements and sending the resulting average and modifying
   * the `sampleCount` field of {@link IMetricTelemetry}.
   * @param  metric input object argument. Only `name` and `average` are mandatory.
   * @param  customProperties custom properties of metric
   */
  public logMetric(metric: IMetricTelemetry, customProperties?: ICustomProperties): void {
    this.insights.trackMetric(metric, customProperties);
  }

  /**
   * Log a dependency call (e.g. ajax)
   * @param dependency dependecy data to log.
   */
  public logDependecyCall(dependecy: IDependencyTelemetry): void {
    this.insights.trackDependencyData(dependecy);
  }

  // #endregion Tracking Functions


  /// Queue Management
  // #region Queue Management

  /**
   * Manually trigger an immediate send of all telemetry still in the buffer using beacon Sender.
   * Fall back to xhr sender if beacon is not supported.
   * @param async decides if the flush should be perforemed asynchronously
   */
  public flushQueue(async?: boolean): void {
    this.insights.flush(async);
  }

  /**
   * Call any functions that were queued before the main script was loaded
   */
  public emptyQueue(): void {
    this.insights.emptyQueue();
  }
  // #endregion Queue Management

  /// Helper Functions
  // #region Helpers

  /**
   * **DO NOT CALL**
   *
   * Telemetry Initializer Function to be added to Telemetry Initializers
   * @param envelope Telemetry Item Data
   */
  private telemetryInitializer(envelope: ITelemetryItem): boolean | void {
    if (typeof screen !== undefined) {
      envelope.tags['ai.device.screenResolution'] = `${screen.width}X${screen.height}`;
    }
    envelope.tags['ai.device.locale'] = navigator.language || 'unknown';
    envelope.tags['ai.user.useragent'] = navigator.userAgent;
  }
  // #endregion Helpers

}
